<template>
  <f7-page :page-content="true" name="autorizacionescondiciones" @page:beforein="cargar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>Autorizaciones de condiciones de facturación</f7-nav-title>
      <f7-nav-right>
        <f7-button icon-f7="arrow_clockwise" text="Recargar" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>Esta pantalla es de ADMIN y DIRECTOR: decide quién autoriza las condiciones de facturación de cada persona.</p>
    </f7-block>

    <div v-else-if="vista" class="ac">
      <div class="ac-principal">
        <!-- A · Quién autoriza a quién: el mismo patrón que el mapa de aprobación de vacaciones. -->
        <div class="ac-cabecera">
          <h2 class="ac-titulo">Quién autoriza a quién</h2>
          <span class="ac-sub">
            A cada persona, quién le autoriza lo que pide en Pago y Finanzas. Quien no tiene a nadie usa los
            generales: {{ vista.textoGenerales }}. Nadie se autoriza a sí mismo.
          </span>
          <!-- Lo configurado que no resuelve se dice aquí, junto a la tabla que ya no lo nombra, y no
               solo en el log del servidor (#905, prueba de ABM del 28/10/2026). -->
          <span v-if="descartados.length" class="ac-aviso-descartados">{{ textoDescartados }}</span>
        </div>

        <div class="ac-barra">
          <input class="ac-buscar" type="search" v-model="busqueda" placeholder="Buscar por nombre, apellidos o usuario" aria-label="Buscar persona" />
          <div class="ac-seg">
            <button v-for="f in filtrosPersona" :key="f.id" type="button" :class="{ elegida: filtroPersona == f.id }"
              @click="filtroPersona = f.id">{{ f.nombre }} ({{ f.cuenta }})</button>
          </div>
        </div>

        <div class="ac-bloque">
          <span class="ac-marcadas">
            Marcadas: {{ marcados.length }}
            <span v-if="marcadosOcultos.length" class="ac-ocultas">({{ marcadosOcultos.length }} fuera del filtro)</span>
          </span>
          <!-- El selector común de personas: se busca escribiendo nombre, apellidos o usuario (§9). -->
          <r-form :key="'bloque-' + formBloque" ref="formBloque" v-slot="{ rform }" :initialData="{ autorizador: null }" class="ac-form-bloque">
            <f7-list no-hairlines class="no-margin">
              <ul>
                <r-autocomplete
                  outline
                  floating-label
                  :dbAdapter="Empleado"
                  label="Autorizador"
                  :form="rform"
                  name="autorizador"
                  idProp="username"
                  labelProp="nombre"
                  prefixProp="username"
                  :filter="{ filter: { activo: true, rolesExcluidos: ['CLIENTE'] } }"
                ></r-autocomplete>
              </ul>
            </f7-list>
          </r-form>
          <div class="ac-seg">
            <button type="button" :disabled="!!motivoNoAsignar('ANADIR')" @click="asignar('ANADIR')">Añadir a las marcadas</button>
            <button type="button" :disabled="!!motivoNoAsignar('QUITAR')" @click="asignar('QUITAR')">Quitar a las marcadas</button>
            <button type="button" :disabled="!!motivoNoAsignar('GENERALES')" @click="asignar('GENERALES')">Dejar con los generales</button>
          </div>
          <span v-if="motivoNoAsignar('ANADIR')" class="ac-nota">{{ motivoNoAsignar('ANADIR') }}</span>
        </div>

        <div class="ac-tabla-envoltorio">
          <table class="ac-tabla">
            <thead>
              <tr>
                <th class="ac-col-check"><input type="checkbox" :checked="todosMarcados" @change="marcarTodos($event)" aria-label="Marcar todas" /></th>
                <th>Persona</th>
                <th>Rol</th>
                <th>Le autoriza</th>
                <th class="ac-col-num">Pendientes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in personasFiltradas" :key="p.username">
                <td class="ac-col-check"><input type="checkbox" :checked="estaMarcado(p.username)" @change="alternar(p.username)" :aria-label="'Marcar a ' + p.nombre" /></td>
                <td>
                  {{ p.nombre }} <span class="ac-usuario">{{ p.username }}</span>
                  <span v-if="!p.activo" class="ac-baja">de baja</span>
                </td>
                <td class="ac-rol">{{ p.rol || '—' }}</td>
                <td>
                  <span v-for="a in p.autorizadores" :key="a.username" class="ac-chip" :class="{ 'ac-chip-baja': !a.activo }">
                    {{ a.nombre }}<template v-if="!a.activo"> (de baja)</template>
                    <button type="button" class="ac-chip-quitar" :aria-label="'Quitar a ' + a.nombre" @click="quitar(p, a)">×</button>
                  </span>
                  <!-- Junto a su fila, no arriba: quien no tiene a nadie asignado usa los generales. -->
                  <span v-if="p.usaGenerales && p.autorizadoresEfectivos" class="ac-nota">Usa los generales: {{ p.autorizadoresEfectivos }}</span>
                  <!-- §9 · es el único general y nadie se autoriza a sí mismo: dice qué falta y dónde. -->
                  <span v-else-if="p.usaGenerales" class="ac-pendientes">
                    Nadie le autoriza: es el único autorizador general. Asígnale a alguien aquí o añade otro general.
                  </span>
                </td>
                <td class="ac-col-num">{{ p.pendientes || '' }}</td>
              </tr>
              <tr v-if="!personasFiltradas.length">
                <td colspan="5" class="ac-vacio">Nadie con ese filtro.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- B · Qué condiciones requieren autorización. Las segundas no salen: se facturan siempre aparte. -->
        <div class="ac-cabecera ac-cabecera-b">
          <h2 class="ac-titulo">Qué condiciones requieren autorización</h2>
          <span class="ac-sub">
            Lo que aquí requiere autorización es lo que Pago y Finanzas pinta en ámbar. Con autorizador propio, la autoriza
            solo él, tenga quien la pide a quien tenga asignado.
          </span>
        </div>
        <div class="ac-condiciones">
          <div class="ac-fila" v-for="c in vista.condiciones" :key="c.clave">
            <div>
              <div class="ac-nombre">{{ c.nombre }}</div>
              <div class="ac-defecto">Por defecto: {{ c.requierePorDefecto ? 'requiere' : 'no requiere' }}</div>
            </div>
            <div class="ac-linea">
              <div class="ac-seg">
                <button type="button" :class="{ elegida: c.requiereAutorizacion }" :disabled="guardando == c.clave" @click="requerir(c, true)">Requiere</button>
                <button type="button" :class="{ elegida: !c.requiereAutorizacion }" :disabled="guardando == c.clave" @click="requerir(c, false)">No requiere</button>
              </div>
              <template v-if="c.requiereAutorizacion">
                <span v-if="!c.autorizadores.length" class="ac-nota">La autoriza quien autoriza a cada persona.</span>
                <span v-for="a in c.autorizadores" :key="a.username" class="ac-chip" :class="{ 'ac-chip-baja': !a.activo }">
                  Solo {{ a.nombre }}<template v-if="!a.activo"> (de baja: va a quien autoriza a la persona)</template>
                  <button type="button" class="ac-chip-quitar" :aria-label="'Quitar a ' + a.nombre" @click="quitarPropio(c, a)">×</button>
                </span>
                <a href="#" class="ac-enlace" @click.prevent="abrirPropio(c)">+ Autorizador propio</a>
              </template>
              <span v-else class="ac-nota">Se aplica en cuanto se guarda.</span>
              <span v-if="c.pendientes" class="ac-pendientes">
                {{ c.pendientes }} pendiente{{ c.pendientes == 1 ? '' : 's' }}: {{ textoAsignadas(c.pendientesAsignadas) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="ac-lateral">
        <div class="ac-tarjeta" :class="vista.totalPendientes ? 'ac-tarjeta-ambar' : 'ac-tarjeta-ok'">
          <div class="ac-tarjeta-titulo">
            {{ vista.totalPendientes ? vista.totalPendientes + ' petición' + (vista.totalPendientes == 1 ? '' : 'es') + ' en la cola' : 'Ninguna petición en la cola' }}
          </div>
          <div v-for="g in vista.pendientesAsignadas" :key="g.autorizadores" class="ac-tarjeta-texto">
            {{ g.pendientes }} asignada{{ g.pendientes == 1 ? '' : 's' }} a {{ g.autorizadores }}
          </div>
          <div v-if="vista.totalPendientes" class="ac-tarjeta-texto">
            Cambiar esta configuración no las mueve: siguen con quien tenían asignado al pedirlas. Lo nuevo que se pida
            irá a quien diga esta pantalla.
          </div>
        </div>
        <!-- Los generales los elige aquí quien administra (Dirección, 27/10/2026). Es lo mínimo hasta
             ACEL-2c (#911), que dará el permiso «autoriza» persona a persona. -->
        <div class="ac-tarjeta">
          <div class="ac-tarjeta-titulo">Autorizadores generales</div>
          <div class="ac-nota">Autorizan a quien no tiene a nadie asignado.</div>
          <div class="ac-tarjeta-texto">
            <span v-for="g in vista.generales" :key="g.username" class="ac-chip">
              {{ g.nombre }}
              <button v-if="vista.generales.length > 1" type="button" class="ac-chip-quitar" :aria-label="'Quitar a ' + g.nombre"
                :disabled="guardando == 'GENERALES'" @click="quitarGeneral(g)">×</button>
            </span>
            <span v-if="!vista.generales.length" class="ac-pendientes">Ahora mismo no hay ninguno de alta: añade al menos uno.</span>
          </div>
          <div v-for="d in descartados" :key="d.nombre" class="ac-pendientes">
            {{ d.nombre }}: {{ d.motivo }}. No autoriza ni se le asigna nada; elige otro con «+ Añadir autorizador general».
          </div>
          <!-- §9 · por qué no se puede quitar el último, junto al chip. -->
          <span v-if="vista.generales.length == 1" class="ac-nota">El último no se puede quitar: sin generales, quien no tiene a nadie asignado no podría pedir autorización. Añade otro antes.</span>
          <a href="#" class="ac-enlace" @click.prevent="abrirGeneral">+ Añadir autorizador general</a>
          <div class="ac-nota">
            {{ vista.generalesDePartida ? 'Son los de partida: nadie los ha cambiado todavía.' : 'Elegidos en esta pantalla; los cambios quedan en el Historial.' }}
            Lo nuevo que se pida va a los que haya aquí.
          </div>
        </div>
        <div class="ac-tarjeta">
          <div class="ac-tarjeta-titulo">Historial</div>
          <div v-if="!vista.historial.length" class="ac-nota">Sin cambios todavía: rige la configuración de partida.</div>
          <div v-for="(h, i) in historialVisible" :key="i" class="ac-historial">
            {{ fechaCorta(h.fecha) }} · {{ h.texto }}
            <div class="ac-nota">{{ h.detalle }}<template v-if="h.detalle && h.nombre"> · </template>{{ h.nombre }}</div>
          </div>
          <a v-if="vista.historial.length > 3" href="#" class="ac-enlace" @click.prevent="historialEntero = !historialEntero">
            {{ historialEntero ? 'Ver solo los últimos' : 'Ver los ' + vista.totalCambios + ' cambios' }}
          </a>
        </div>
      </div>
    </div>

    <!-- El autorizador propio de una condición se elige con el mismo selector común. -->
    <f7-popup :opened="!!condicionEditada" @popup:closed="condicionEditada = null" class="ac-popup">
      <f7-page v-if="condicionEditada">
        <f7-navbar :title="condicionEditada.generales ? 'Autorizador general' : 'Autorizador propio · ' + condicionEditada.nombre">
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block>
          <p class="ac-sub" v-if="condicionEditada.generales">Autorizará a quien no tenga a nadie asignado, junto con los que ya hay.</p>
          <p class="ac-sub" v-else>Esta condición la autorizará solo quien elijas, tenga quien la pide a quien tenga asignado.</p>
          <r-form :key="'propio-' + formPropio" ref="formPropio" v-slot="{ rform }" :initialData="{ autorizador: null }">
            <f7-list no-hairlines class="no-margin">
              <ul>
                <r-autocomplete
                  outline
                  floating-label
                  :dbAdapter="Empleado"
                  label="Autorizador"
                  :form="rform"
                  name="autorizador"
                  idProp="username"
                  labelProp="nombre"
                  prefixProp="username"
                  :filter="{ filter: { activo: true, rolesExcluidos: ['CLIENTE'] } }"
                ></r-autocomplete>
              </ul>
            </f7-list>
          </r-form>
          <f7-button fill class="margin-top" @click="anadirPropio">Añadir</f7-button>
        </f7-block>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<script>
import rForm from "./../../components/rForm.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import Empleado from "./../../js/db/Empleado.js";
import Autorizaciones from "./../../js/db/Autorizaciones.js";
import { fechaCorta } from "./../../js/condicionesFacturacion.js";

/**
 * Ajustes › Autorizaciones de condiciones de facturación (#905, alcance ampliado por Dirección el
 * 27/10/2026). Dos bloques en la misma pantalla:
 *
 * - A · Quién autoriza a quién, con el mismo diseño y comportamiento que el mapa de aprobación de
 *   vacaciones (`mapa-aprobacion.vue`): plantilla entera de una vez, búsqueda y filtro en memoria,
 *   marcar personas y asignar en bloque con el selector común. Lo marcado y oculto por el filtro se
 *   cuenta y se confirma antes de asignar, como allí.
 * - B · Qué condiciones requieren autorización, y si tienen autorizador propio.
 * - Y en la columna derecha, los autorizadores generales, que también se eligen aquí (Dirección,
 *   27/10/2026; lo mínimo hasta el permiso por persona de ACEL-2c, #911).
 *
 * Todo lo decide y lo vuelve a comprobar la API; tras cada cambio se relee la pantalla entera para
 * que lo que se ve sea lo que hay.
 */
export default {
  components: { rForm, rAutocomplete },
  data: function () {
    return {
      Empleado,
      vista: null,
      error403: false,
      busqueda: '',
      filtroPersona: 'TODAS',
      marcados: [],
      formBloque: 0,
      formPropio: 0,
      condicionEditada: null,
      guardando: null,
      historialEntero: false,
      bloqueos: 0
    };
  },
  watch: {
    // Lo marcado es lo que se está viendo, como en el mapa de aprobación.
    busqueda: function () { this.podarMarcados(); },
    filtroPersona: function () { this.podarMarcados(); }
  },
  computed: {
    personas: function () {
      return (this.vista && this.vista.personas) || [];
    },
    descartados: function () {
      return (this.vista && this.vista.generalesDescartados) || [];
    },
    textoDescartados: function () {
      const d = this.descartados;
      const uno = d.length == 1;
      return d.length + (uno ? ' autorizador general configurado no autoriza' : ' autorizadores generales configurados no autorizan')
        + ': ' + d.map(x => x.nombre + ' (' + x.motivo + ')').join('; ') + '. Esta tabla no ' + (uno ? 'lo' : 'los')
        + ' nombra; los generales se eligen en «Autorizadores generales», a la derecha.';
    },
    filtrosPersona: function () {
      const conAlguien = this.personas.filter(p => !p.usaGenerales).length;
      return [
        { id: 'TODAS', nombre: 'Todas', cuenta: this.personas.length },
        { id: 'ASIGNADO', nombre: 'Con autorizador', cuenta: conAlguien },
        { id: 'GENERALES', nombre: 'Con los generales', cuenta: this.personas.length - conAlguien }
      ];
    },
    personasFiltradas: function () {
      const self = this;
      let lista = self.personas;
      if (self.filtroPersona == 'ASIGNADO') lista = lista.filter(p => !p.usaGenerales);
      else if (self.filtroPersona == 'GENERALES') lista = lista.filter(p => p.usaGenerales);
      const q = self.normalizar(self.busqueda);
      if (!q) return lista;
      return lista.filter(p => self.normalizar((p.nombre || '') + ' ' + (p.username || '')).indexOf(q) !== -1);
    },
    todosMarcados: function () {
      const filas = this.personasFiltradas;
      return filas.length > 0 && filas.every(p => this.estaMarcado(p.username));
    },
    marcadosOcultos: function () {
      return this.marcados.filter(u => !this.personasFiltradas.some(p => p.username == u));
    },
    historialVisible: function () {
      const h = (this.vista && this.vista.historial) || [];
      return this.historialEntero ? h : h.slice(0, 3);
    }
  },
  methods: {
    fechaCorta: fechaCorta,
    normalizar: function (t) {
      return (t || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    },
    es403: function (e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    cargar: function () {
      const self = this;
      self.bloquear();
      return Autorizaciones.configuracion().then(function (res) {
        self.vista = res;
        self.error403 = false;
        self.podarMarcados();
      }).catch(function (e) {
        if (self.es403(e)) self.error403 = true;
        else self.$f7.dialog.alert((e && e.message) || 'No se ha podido leer la configuración.');
      }).then(function () {
        self.desbloquear();
      });
    },
    estaMarcado: function (username) {
      return this.marcados.indexOf(username) !== -1;
    },
    alternar: function (username) {
      const i = this.marcados.indexOf(username);
      if (i === -1) this.marcados.push(username);
      else this.marcados.splice(i, 1);
    },
    podarMarcados: function () {
      const visibles = this.personasFiltradas.map(p => p.username);
      const quedan = this.marcados.filter(u => visibles.indexOf(u) !== -1);
      if (quedan.length !== this.marcados.length) this.marcados = quedan;
    },
    marcarTodos: function (evento) {
      const self = this;
      if (evento.target.checked) {
        self.personasFiltradas.forEach(p => { if (!self.estaMarcado(p.username)) self.marcados.push(p.username); });
      } else {
        self.marcados = self.marcados.filter(u => !self.personasFiltradas.some(p => p.username == u));
      }
    },
    autorizadorElegido: function () {
      const f = this.$refs.formBloque;
      return f && f.formData ? f.formData.autorizador || null : null;
    },
    /** Por qué no se puede pulsar cada botón del bloque, junto a ellos (§9). */
    motivoNoAsignar: function (accion) {
      if (!this.marcados.length) return 'Marca antes a las personas en la tabla.';
      if (accion != 'GENERALES' && !this.autorizadorElegido()) return 'Elige antes el autorizador.';
      return null;
    },
    asignar: function (accion) {
      const self = this;
      if (self.motivoNoAsignar(accion)) return;
      const ocultos = self.marcadosOcultos;
      if (!ocultos.length) {
        self.enviar(self.marcados.slice(), accion, self.autorizadorElegido());
        return;
      }
      self.$f7.dialog.confirm('Hay ' + ocultos.length + ' persona(s) marcadas que el filtro no enseña: ' + ocultos.join(', ')
        + '. ¿Cambiar también las suyas?', 'Quién autoriza a quién', function () {
        self.enviar(self.marcados.slice(), accion, self.autorizadorElegido());
      });
    },
    quitar: function (persona, autorizador) {
      this.enviar([persona.username], 'QUITAR', autorizador.username);
    },
    enviar: function (usernames, accion, autorizador) {
      const self = this;
      self.bloquear();
      Autorizaciones.asignarAutorizadores(usernames, accion, autorizador).then(function (res) {
        res = res || {};
        self.marcados = [];
        self.formBloque++;
        return self.cargar().then(function () {
          const texto = (res.actualizados || 0) + ' cambiada(s), ' + (res.sinCambios || 0) + ' sin cambios.';
          if (res.errores && res.errores.length) {
            self.$f7.dialog.alert(texto + '<br><br>' + res.errores.join('<br>'), 'Quién autoriza a quién');
          } else {
            self.$f7.toast.create({ text: texto, position: 'center', closeTimeout: 2000, destroyOnClose: true }).open();
          }
        });
      }).catch(function (e) {
        self.$f7.dialog.alert((e && e.message) || 'No se ha podido guardar.');
      }).then(function () {
        self.desbloquear();
      });
    },
    requerir: function (c, requiere) {
      if (c.requiereAutorizacion === requiere) return;
      this.guardarCondicion(c, requiere, requiere ? c.autorizadores.map(a => a.username) : []);
    },
    abrirPropio: function (c) {
      this.formPropio++;
      this.condicionEditada = c;
    },
    anadirPropio: function () {
      const c = this.condicionEditada;
      const f = this.$refs.formPropio;
      const elegido = f && f.formData ? f.formData.autorizador : null;
      if (!elegido) {
        this.$f7.dialog.alert('Elige el autorizador.');
        return;
      }
      this.condicionEditada = null;
      if (c.generales) {
        const generales = this.vista.generales.map(g => g.username);
        if (generales.indexOf(elegido) === -1) generales.push(elegido);
        this.guardarGenerales(generales);
        return;
      }
      const lista = c.autorizadores.map(a => a.username);
      if (lista.indexOf(elegido) === -1) lista.push(elegido);
      this.guardarCondicion(c, true, lista);
    },
    abrirGeneral: function () {
      this.formPropio++;
      this.condicionEditada = { generales: true };
    },
    quitarGeneral: function (g) {
      this.guardarGenerales(this.vista.generales.map(x => x.username).filter(u => u != g.username));
    },
    guardarGenerales: function (usernames) {
      const self = this;
      self.guardando = 'GENERALES';
      self.bloquear();
      Autorizaciones.guardarGenerales(usernames).then(function (res) {
        if (res) self.vista = res;
      }).catch(function (e) {
        self.$f7.dialog.alert((e && e.message) || 'No se ha podido guardar.');
        self.cargar();
      }).then(function () {
        self.guardando = null;
        self.desbloquear();
      });
    },
    quitarPropio: function (c, a) {
      this.guardarCondicion(c, true, c.autorizadores.map(x => x.username).filter(u => u != a.username));
    },
    guardarCondicion: function (c, requiere, autorizadores) {
      const self = this;
      self.guardando = c.clave;
      self.bloquear();
      Autorizaciones.guardarCondicion(c.clave, requiere, autorizadores).then(function (res) {
        if (res) self.vista = res;
      }).catch(function (e) {
        self.$f7.dialog.alert((e && e.message) || 'No se ha podido guardar.');
        self.cargar();
      }).then(function () {
        self.guardando = null;
        self.desbloquear();
      });
    },
    textoAsignadas: function (grupos) {
      return (grupos || []).map(g => g.pendientes + ' a ' + g.autorizadores).join(' · ');
    },
    bloquear: function () {
      this.bloqueos++;
      if (this.bloqueos === 1) this.$f7.preloader.show();
    },
    desbloquear: function () {
      this.bloqueos = Math.max(0, this.bloqueos - 1);
      if (this.bloqueos === 0) this.$f7.preloader.hide();
    }
  }
};
</script>

<style scoped>
/* La disposición compacta de la maqueta de Pago y Finanzas (#905, 27/10/2026): filas de 44 px,
   controles segmentados de 28 px en línea y columna derecha de 300 px. */
.ac { display: flex; gap: 24px; padding: 16px 24px; align-items: flex-start; color: #1c1c3a; }
.ac-principal { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.ac-lateral { width: 300px; flex: none; display: flex; flex-direction: column; gap: 12px; }
@media (max-width: 900px) {
  .ac { flex-direction: column; }
  .ac-lateral { width: 100%; }
}
.ac-cabecera { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.ac-cabecera-b { margin-top: 8px; }
.ac-titulo { margin: 0; font-size: 16px; font-weight: 600; }
.ac-sub { font-size: 12px; color: #565a85; flex: 1; min-width: 0; }
.ac-nota { font-size: 12px; color: #565a85; }
.ac-barra { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ac-buscar { height: 28px; width: 280px; box-sizing: border-box; border: 1px solid #c4c6e4; border-radius: 6px; padding: 0 8px; font-size: 13px; font-family: inherit; }

.ac-seg { display: inline-flex; flex: none; border: 1px solid #c4c6e4; border-radius: 6px; overflow: hidden; }
/* Framework7 pone `button { width: 100% }` a todos los botones: sin un ancho propio, cada opción
   ocupa el control entero y el `overflow: hidden` esconde la segunda. Así se quedó oculto «No requiere»
   (Dirección, preview-10, 28/10/2026). */
.ac-seg button { width: auto; height: 28px; line-height: 28px; padding: 0 12px; border: 0; background: #fff; color: #1c1c3a; font-size: 13px; font-family: inherit; white-space: nowrap; flex: none; cursor: pointer; }
.ac-seg button + button { border-left: 1px solid #c4c6e4; }
.ac-seg button.elegida { background: #22224f; color: #fff; font-weight: 500; }
.ac-seg button:disabled { cursor: default; color: #8a8db0; }
.ac-seg button.elegida:disabled { color: #fff; opacity: 0.7; }

.ac-bloque { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; border: 1px solid #dcdcf0; border-radius: 8px; padding: 4px 12px; }
.ac-marcadas { font-size: 13px; font-weight: 600; white-space: nowrap; }
.ac-ocultas { font-weight: 400; color: #b26a00; }
.ac-form-bloque { width: 320px; max-width: 100%; }

.ac-tabla-envoltorio { border: 1px solid #dcdcf0; border-radius: 8px; max-height: 360px; overflow: auto; }
.ac-tabla { width: 100%; border-collapse: collapse; font-size: 13px; }
.ac-tabla th { position: sticky; top: 0; background: #f4f5fb; text-align: left; font-weight: 600; padding: 6px 10px; border-bottom: 1px solid #dcdcf0; }
.ac-tabla td { padding: 4px 10px; border-bottom: 1px solid #ececf6; height: 36px; box-sizing: border-box; }
.ac-col-check { width: 28px; }
.ac-col-num { text-align: right; width: 80px; }
.ac-usuario { font-size: 11px; color: #565a85; margin-left: 4px; }
.ac-baja { font-size: 11px; color: #a33b1b; margin-left: 4px; }
.ac-rol { color: #565a85; white-space: nowrap; }
.ac-vacio { text-align: center; color: #565a85; padding: 16px; }
.ac-chip { display: inline-flex; align-items: center; gap: 4px; background: #e9e9fa; color: #22224f; border-radius: 10px; padding: 1px 4px 1px 8px; font-size: 12px; margin: 2px 4px 2px 0; white-space: nowrap; }
.ac-chip-baja { background: #fdf1c7; color: #6b5000; }
.ac-chip-quitar { width: auto; border: 0; background: transparent; color: inherit; cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }

.ac-condiciones { border: 1px solid #dcdcf0; border-radius: 8px; }
.ac-fila { display: grid; grid-template-columns: 250px 1fr; align-items: center; padding: 6px 14px; min-height: 44px; box-sizing: border-box; border-bottom: 1px solid #ececf6; }
.ac-fila:last-child { border-bottom: none; }
.ac-nombre { font-size: 13px; font-weight: 600; }
.ac-defecto { font-size: 11px; color: #565a85; }
.ac-linea { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; row-gap: 4px; min-width: 0; }
.ac-enlace { font-size: 12px; color: #3f3f8f; white-space: nowrap; }
.ac-pendientes { font-size: 12px; color: #6b5000; }
.ac-aviso-descartados { flex-basis: 100%; margin-top: 4px; padding: 4px 8px; font-size: 12px; color: #4d3b00; background: #fff7dc; border-left: 3px solid #c99a00; }

.ac-tarjeta { border: 1px solid #dcdcf0; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 6px; background: #fff; }
.ac-tarjeta-titulo { font-size: 13px; font-weight: 600; }
.ac-tarjeta-texto { font-size: 12px; line-height: 1.4; }
.ac-tarjeta-ambar { background: #fff7dc; border-color: #e8cf7a; color: #4d3b00; }
.ac-tarjeta-ok { background: #f2faf4; border-color: #cfe3d3; color: #1f5130; }
.ac-historial { font-size: 12px; line-height: 1.4; }
</style>
